<template>
  <div class="flight-map-container">
    <div ref="mapContainer" class="map-container"></div>
  </div>
</template>

<script>
// 计算两点之间的角度（bearing，单位：度）
function getAngle(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;
  const dLon = toRad(lng2 - lng1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  let brng = Math.atan2(y, x);
  brng = toDeg(brng);
  return (brng + 360) % 360;
}

export default {
  name: "FlightMap",
  props: {
    track: {
      type: Array,
      required: true,
    },
    speed: {
      type: Number,
      default: 100,
    },
    mapType: {
      type: String,
      default: "img", // vec(矢量)、img(影像)、ter(地形)
    },
  },
  data() {
    return {
      timer: null,
      map: null,
      polyline: null,
      marker: null,
      currentIndex: 0,
      isPlaying: false,
      playbackSpeed: 1,
      tiandituKey: "d083e4cf30bfc438ef93436c10c2c20a", // 需要替换为实际密钥
      totalDistance: 0,
      currentSpeed: 0,
      startTime: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    initMap() {
      // 确保 Leaflet 已加载
      if (typeof L === "undefined") {
        console.error("Leaflet 未加载");
        return;
      }

      // 初始化地图
      this.map = L.map(this.$refs.mapContainer, {
        zoom: 11,
        center: this.track[0] || [39.9042, 116.4074], // 默认北京
        zoomControl: false, // 隐藏缩放控件
        attributionControl: false, // 隐藏版权信息
      });

      // 添加天地图底图
      const baseLayer = L.tileLayer(this.getTiandituBaseUrl(), {
        attribution: "© 天地图",
        retry: {
          maxAttempts: 3, // 重试3次
          delay: 1000, // 每次重试间隔1秒
        },
      });
      baseLayer.addTo(this.map);

      // 添加天地图注记
      const labelLayer = L.tileLayer(this.getTiandituLabelUrl(), {
        attribution: "© 天地图",
        retry: {
          maxAttempts: 3, // 重试3次
          delay: 1000, // 每次重试间隔1秒
        },
      });
      labelLayer.addTo(this.map);

      // 绘制轨迹线
      if (this.track.length > 1) {
        this.polyline = L.polyline(this.track, {
          color: "#007bff",
          weight: 3,
          opacity: 0.8,
        }).addTo(this.map);
      }

      // 添加飞机标记
      this.marker = L.marker(this.track[0] || [39.9042, 116.4074], {
        icon: this.createPlaneIcon(),
      }).addTo(this.map);

      // 调整地图视野
      if (this.track.length > 1) {
        this.map.fitBounds(this.polyline.getBounds(), {
          padding: [0, 0],
        });
        this.map.setZoom(12); // 强制设置为15级
        setTimeout(() => {
          this.map.setZoom(11); // 强制设置为15级
        }, 500);
        // 开始轨迹回放
        this.startReplay();
      }
    },

    createPlaneIcon() {
      // 使用SVG飞机图标，头部朝上
      return L.divIcon({
        className: "plane-marker",
        html: `<div style="transform-origin: center; transform: rotate(0deg);">
            <svg t="1752129264103" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5159" width="20" height="32"><path d="M599.06048 831.309824l12.106752-193.404928 372.860928 184.430592L984.02816 710.206464 617.906176 367.33952 617.906176 151.638016c0-56.974336-46.188544-143.064064-103.158784-143.064064-56.974336 0-103.158784 86.089728-103.158784 143.064064L411.588608 367.33952 45.461504 710.206464l0 112.129024 366.660608-184.430592 14.999552 209.27488c0 5.05344 0.594944 9.892864 1.124352 14.749696l-66.591744 60.348416 0 66.587648 153.986048-50.879488 2.43712-0.80896 147.439616 51.688448 0-66.587648-68.758528-62.253056L599.06048 831.309824z" fill="#FF9000" p-id="5160"></path></svg>
                </div>
                `,
        iconSize: [24, 24],
        iconAnchor: [12, 12], // 锚点在中心
      });
      //   // 创建飞机图标
      //   return L.divIcon({
      //     className: "plane-marker",
      //     html: "✈️",
      //     iconSize: [20, 20],
      //     iconAnchor: [10, 10],
      //   });
    },

    getTiandituBaseUrl() {
      const layerMap = {
        vec: "vec",
        img: "img",
        ter: "ter",
      };
      const layer = layerMap[this.mapType] || "img";
      return `https://t0.tianditu.gov.cn/${layer}_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${layer}&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${this.tiandituKey}`;
    },

    getTiandituLabelUrl() {
      const labelMap = {
        vec: "cva",
        img: "cia",
        ter: "cta",
      };
      const label = labelMap[this.mapType] || "cva";
      return `https://t0.tianditu.gov.cn/${label}_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${label}&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${this.tiandituKey}`;
    },

    startReplay() {
      if (this.isPlaying || this.currentIndex >= this.track.length - 1) return;

      this.isPlaying = true;
      this.startTime = performance.now();

      // 验证起点有效性
      const startPoint = this.track[this.currentIndex];

      // 重置标记位置到起点
      this.marker.setLatLng(startPoint);

      this.moveToNextPoint();
    },
    calculateTotalDistance() {
      let total = 0;
      for (let i = 1; i < this.track.length; i++) {
        const p1 = this.track[i - 1];
        const p2 = this.track[i];
        total += this.calculateDistance(p1[0], p1[1], p2[0], p2[1]);
      }
      this.totalDistance = total;
    },
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371e3; // 地球半径，单位：米
      const φ1 = (lat1 * Math.PI) / 180;
      const φ2 = (lat2 * Math.PI) / 180;
      const Δφ = ((lat2 - lat1) * Math.PI) / 180;
      const Δλ = ((lon2 - lon1) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return (R * c) / 1000; // 返回千米
    },
    moveToNextPoint() {
      if (this.currentIndex >= this.track.length - 1) {
        this.isPlaying = false;
        this.$emit("playback-complete");
        return;
      }

      const start = this.track[this.currentIndex];
      const end = this.track[this.currentIndex + 1];
      const duration = 5000; // 速度影响持续时间
      // 平滑移动标记
      this.smoothMoveMarker(start, end, duration, () => {
        this.currentIndex++;
        if (this.isPlaying) {
          this.moveToNextPoint();
        }
      });
    },
    // 在Vue methods中
    smoothMoveMarker(start, end, duration = 1000, callback) {
      const startTime = performance.now();
      const animate = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / duration, 1); // 0~1
        const lat = start.lat + (end.lat - start.lat) * t;
        const lng = start.lng + (end.lng - start.lng) * t;
        this.marker.setLatLng([lat, lng]);

        // 用当前位置和下一个点计算角度
        let angle = 0;
        if (t < 1) {
          angle = getAngle(lat, lng, end.lat, end.lng);
        } else {
          angle = getAngle(start.lat, start.lng, end.lat, end.lng);
        }

        if (this.marker && this.marker._icon) {
          // 更新飞机图标旋转
          const planeElement = this.marker._icon.querySelector("div");
          if (planeElement) {
            planeElement.style.transform = `rotate(${angle}deg)`;
          }
        }

        if (t < 1) {
          requestAnimationFrame(animate);
        } else if (callback) {
          callback();
        }
      };
      requestAnimationFrame(animate);
    },

    cleanup() {
      this.isPlaying = false;
      if (this.timer) {
        clearInterval(this.timer);
      }
      if (this.map) {
        this.map.remove();
      }
    },
  },
  watch: {
    track: {
      handler() {
        this.cleanup();
        this.$nextTick(() => {
          this.initMap();
        });
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.flight-map-container {
  width: 100%;
  height: 100%;
}

.map-container {
  width: 100%;
  height: 145px; /* 与原缩略图高度一致 */
  border-radius: 8px;
  overflow: hidden;
}

/* 飞机图标样式 */
:deep(.plane-marker) {
  background: none;
  border: none;
  font-size: 20px;
  text-align: center;
  line-height: 20px;
}
</style>
